/**
 * Confirmation modal for changes that are hard to walk back — closing a
 * ticket, approving a refund, writing off stock.
 *
 * Deliberately small: a title, a sentence of consequence, an optional
 * summary block (amount, line count, who gets notified) and two buttons.
 * The confirm button carries the pending state so the caller does not have
 * to disable it by hand.
 */
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  /** Extra context — an amount, the affected lines, who will be notified. */
  details?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `destructive` for irreversible removals, `primary` for approvals. */
  tone?: 'primary' | 'destructive';
  isPending?: boolean;
  onConfirm: () => void | Promise<void>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  details,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  isPending = false,
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => (isPending ? null : onOpenChange(next))}>
      <DialogContent className="max-w-md">
        <div className="space-y-4">
          <div>
            <DialogTitle asChild>
              <h2 className="font-display text-base font-semibold">{title}</h2>
            </DialogTitle>
            {description ? (
              <p className="mt-1 text-xs text-muted-foreground">{description}</p>
            ) : null}
          </div>

          {details ? (
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
              {details}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2">
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="sm" disabled={isPending}>
                {cancelLabel}
              </Button>
            </DialogPrimitive.Close>
            <Button
              size="sm"
              variant={tone}
              onClick={() => void onConfirm()}
              disabled={isPending}
              className="gap-1"
            >
              {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
              {confirmLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
